// components/invoices/index.js
import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, limit, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import InvoicesHeader from './InvoicesHeader';
import InvoicesFilterBar from './InvoicesFilterBar';
import InvoicesTable from './InvoicesTable';
import styles from './Invoices.module.css';

const InvoicesContainer = () => {
  const { jeweller } = useAuth();

  // State management
  const [activeFilter, setActiveFilter] = useState('All');
  const [allInvoices, setAllInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [projectsData, setProjectsData] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Fetch projects data for all unique project IDs
  const fetchProjectsData = async (projectIds) => {
    const projectsLookup = {};
    
    for (const projectId of projectIds) {
      if (!projectId) continue;
      
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (projectDoc.exists()) {
          projectsLookup[projectId] = projectDoc.data();
        }
      } catch (error) {
        console.error(`Error fetching project ${projectId}:`, error);
      }
    }
    
    return projectsLookup;
  };

  // Helper to check if an invoice is overdue
  const isInvoiceOverdue = (invoice) => {
    if (invoice.status === 'paid') return false;
    if (!invoice.dueDate) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dueDate = new Date(invoice.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    
    return dueDate < today;
  };

  // Filter invoices based on active filter
  const applyFilters = (invoices) => {
    let filtered = invoices;

    // Apply customer filter first
    if (selectedCustomer) {
      filtered = filtered.filter(invoice => invoice.customerId === selectedCustomer.id);
    }

    // Apply status filter
    switch (activeFilter) {
      case 'Open':
        filtered = filtered.filter(invoice => 
          invoice.status !== 'paid' && !isInvoiceOverdue(invoice)
        );
        break;
      case 'Overdue':
        filtered = filtered.filter(invoice => isInvoiceOverdue(invoice));
        break;
      case 'Paid':
        filtered = filtered.filter(invoice => invoice.status === 'paid');
        break;
      case 'All':
      default:
        break;
    }

    return filtered;
  };

  // Main effect to fetch invoices
  useEffect(() => {
    const fetchInvoices = async () => {
      console.log("fetchInvoices called, jeweller:", jeweller);
      if (!jeweller?.id) {
        console.log("No jeweller ID found");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const invoicesCollection = collection(db, "invoices");
        const finalQuery = query(
          invoicesCollection, 
          where("jewellerId", "==", jeweller.id)
        );

        console.log("Query created, setting up listener");
        
        // Set up real-time listener
        const unsubscribe = onSnapshot(finalQuery, async (querySnapshot) => {
          const fetchedInvoices = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          fetchedInvoices.sort((a, b) => {
            const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return bDate - aDate; // Descending order (newest first)
          });

          // Get unique project IDs
          const projectIds = [...new Set(fetchedInvoices.map(invoice => invoice.projectId).filter(Boolean))];
          
          // Fetch projects data
          const projects = await fetchProjectsData(projectIds);
          setProjectsData(projects);
          
          // Store all invoices
          setAllInvoices(fetchedInvoices);
          setIsLoading(false);
          
          console.log("Invoice data updated in real-time!");
        });

        // Cleanup function
        return () => unsubscribe();

      } catch (error) {
        console.error("Error setting up real-time listener:", error);
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [jeweller]);

  // Separate effect to apply filters
  useEffect(() => {
    const filtered = applyFilters(allInvoices);
    setFilteredInvoices(filtered);
    console.log(`Applied ${activeFilter} filter: ${filtered.length} invoices found`);
  }, [allInvoices, activeFilter, selectedCustomer]);

  return (
    <div className={styles.invoicesPage}>
      <InvoicesHeader />
      
      <InvoicesFilterBar 
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        jewellerId={jeweller?.id}
      />
      
      <InvoicesTable 
        filteredInvoices={filteredInvoices}
        projectsData={projectsData}
        isLoading={isLoading}
      />
    </div>
  );
};

export default InvoicesContainer;